import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import { useBatchContext } from '@/app/BatchContext';

interface FacilityDto {
  facility_id: string;
  facility_name: string;
  facility_role: string;
  active_flag: boolean;
}

interface MonthlyBatchDto {
  monthly_batch_id: string;
  facility_id: string;
  year_month: string;
  status: string;
}

const NS = 'batches';

const ymRegex = /^\d{4}-\d{2}$/;

interface FormInput {
  facility_id: string;
  year_month: string;
}

interface CreateBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When true, after create the dialog auto-selects the new batch in
   *  BatchContext and navigates to its detail page. */
  navigateAfterCreate?: boolean;
}

export const CreateBatchDialog = ({
  open,
  onOpenChange,
  navigateAfterCreate = false,
}: CreateBatchDialogProps) => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { setBatchId } = useBatchContext();
  const schema = useMemo(
    () =>
      z.object({
        facility_id: z
          .string()
          .min(1, t('efs.batches.create.validation.facility_required', { ns: NS })),
        year_month: z
          .string()
          .min(1, t('efs.batches.create.validation.year_month_required', { ns: NS }))
          .regex(ymRegex, t('efs.batches.create.validation.year_month_format', { ns: NS })),
      }),
    [t],
  );

  const facilitiesQuery = useQuery<FacilityDto[]>({
    queryKey: ['facilities', 'list'],
    queryFn: async () => {
      const r = await apiClient.get<ApiSuccess<FacilityDto[]>>('/facilities');
      return r.data.data ?? [];
    },
    enabled: open,
  });
  const facilities = facilitiesQuery.data ?? [];

  const defaultMonth = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const form = useForm<FormInput>({
    resolver: zodResolver(schema),
    defaultValues: { facility_id: '', year_month: defaultMonth },
  });

  // Depend on a stable primitive, not the `facilities` array — `?? []` makes a
  // fresh reference each render, which (with form.setValue re-rendering) caused
  // an infinite update loop. The lone-facility id is the only value we read.
  const soleFacilityId = facilities.length === 1 ? facilities[0].facility_id : null;

  useEffect(() => {
    if (!open) {
      form.reset({ facility_id: '', year_month: defaultMonth });
    } else if (soleFacilityId) {
      form.setValue('facility_id', soleFacilityId);
    }
  }, [open, soleFacilityId, form, defaultMonth]);

  const create = useMutation({
    mutationFn: async (body: FormInput) => {
      const r = await apiClient.post<ApiSuccess<MonthlyBatchDto>>('/monthly-batches', body);
      return r.data.data;
    },
    onSuccess: (dto) => {
      void qc.invalidateQueries({ queryKey: ['monthly-batches'] });
      setBatchId(dto.monthly_batch_id);
      onOpenChange(false);
      if (navigateAfterCreate) {
        navigate(`/batches/${dto.monthly_batch_id}`);
      }
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await create.mutateAsync(values);
  });

  const errorMsg = create.error instanceof Error ? create.error.message : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('efs.batches.create.title', {
              ns: NS,
              defaultValue: '新しい月合計表を作成',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.batches.create.description', {
              ns: NS,
              defaultValue:
                '月合計表は「ある施設のある月のすべての入荷・仕分け・解体・出荷を束ねる集計単位」です。',
            })}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <FormField
              control={form.control}
              name="year_month"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.batches.create.fields.year_month', {
                      ns: NS,
                      defaultValue: '対象月 (YYYY-MM)',
                    })}
                  </FormLabel>
                  <FormControl>
                    <Input type="month" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Facility picker is hidden when there's only one facility —
                the only option is auto-selected in the effect above. */}
            {facilities.length > 1 ? (
              <FormField
                control={form.control}
                name="facility_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('efs.batches.create.fields.facility', {
                        ns: NS,
                        defaultValue: '対象施設',
                      })}
                    </FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={facilitiesQuery.isLoading || facilities.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              facilitiesQuery.isLoading
                                ? t('efs.common.messages.loading')
                                : t('efs.batches.create.fields.facility_placeholder', {
                                    ns: NS,
                                    defaultValue: '施設を選択',
                                  })
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {facilities.map((f) => (
                            <SelectItem key={f.facility_id} value={f.facility_id}>
                              {f.facility_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}

            {errorMsg ? (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-900 dark:bg-red-950 dark:text-red-100">
                {errorMsg}
              </p>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={create.isPending}
              >
                {t('efs.common.actions.cancel', { ns: 'common' })}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.batches.create.submit', {
                  ns: NS,
                  defaultValue: '月合計表を作成',
                })}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
